import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Fab, Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  ColorSwatch,
  PermissionArea,
  PrimitiveTable,
  type TableFilter,
  Text,
} from '@pbt/pbt-ui-components'

import FeatureToggle from '~/constants/featureToggle'
import {
  fetchAppointmentTypesList,
  setAppointmentTypesListFilters,
  setAppointmentTypesListSortings,
} from '~/store/actions/appointmentTypes'
import {
  getAppointmentTypesIsLoading,
  getAppointmentTypesListFilters,
  getAppointmentTypesListSortings,
  getAppointmentTypesMap,
} from '~/store/reducers/appointmentTypes'
import { getCRUDByArea, getCurrentBusinessId } from '~/store/reducers/auth'
import {
  getAppointmentTypes,
  getFeatureToggle,
} from '~/store/reducers/constants'

import AppointmentConfigurationRow, {
  NEW_TYPE_PLACEHOLDER_ID,
} from './AppointmentConfigurationRow'
import { getAppointmentConfigurationColumns } from './getAppointmentConfigurationColumns'
import { getAppointmentConfigurationColumnsV1 } from './getAppointmentConfigurationColumnsV1'
import {
  applyFilters,
  applySortings,
  Filters,
  getFilterValueStrings,
  getMapAsArray,
} from './utils'

const useStyles = makeStyles(
  (theme) => ({
    tableHeader: {
      border: theme.constants.tableBorder,
      backgroundColor: theme.colors.tableBackground,
    },
    addButton: {
      minWidth: 120,
      height: 40,
    },
    rowContainer: {
      border: 'none',
    },
    rowContainerNoColoring: {
      borderTop: 'none',
      borderBottom: 'none',
    },
  }),
  { name: 'AppointmentConfigurationPage' },
)

// Use the hex value for each color filter to generate a color swatch
const addComponentsToFilters = (
  filters: Record<string, TableFilter>,
): Record<string, TableFilter> => {
  const newFilters = R.clone(filters)

  if (!R.isNil(newFilters[Filters.COLOR]?.value)) {
    const colorStrings = getFilterValueStrings(newFilters[Filters.COLOR])
    newFilters[Filters.COLOR].component = (
      <Grid container item wrap="nowrap">
        {colorStrings.map((color) => (
          <ColorSwatch
            color={color}
            key={color}
            noColorValueEnabled={!color.length}
          />
        ))}
      </Grid>
    )
  }

  return newFilters
}

const AppointmentConfigurationPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { t } = useTranslation('Common')

  const isLoading = useSelector(getAppointmentTypesIsLoading)
  const map = useSelector(getAppointmentTypesMap)
  const filters = useSelector(getAppointmentTypesListFilters)
  const sortings = useSelector(getAppointmentTypesListSortings)
  const currentBusinessId = useSelector(getCurrentBusinessId)
  const AppointmentEventTypes = useSelector(getAppointmentTypes)
  const permissions = useSelector(
    getCRUDByArea(PermissionArea.BUSINESS_APPOINTMENT_TYPE),
  )
  const isCvcRolesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_ROLES),
  )
  const isDropOffEnabled = useSelector(
    getFeatureToggle(FeatureToggle.APPOINTMENT_DROP_OFF),
  )

  const columns = useMemo(
    () =>
      isDropOffEnabled
        ? getAppointmentConfigurationColumns()
        : getAppointmentConfigurationColumnsV1(),
    [isDropOffEnabled],
  )

  const [showAddAppontmentTypeRow, setShowAddAppontmentTypeRow] =
    useState(false)

  // Due to reduced scope of RHAP-7 search filters are not currently applied on API call
  const handleFetchList = (businessId: string) => {
    dispatch(
      fetchAppointmentTypesList({
        businessId,
        searchFilter: {
          from: 0,
          to: 200,
        },
      }),
    )
  }

  const handleClearFilters = () => {
    dispatch(setAppointmentTypesListSortings({}))
    dispatch(setAppointmentTypesListFilters({}))
  }

  useEffect(
    () => () => {
      handleClearFilters()
    },
    [],
  )

  useEffect(() => {
    if (currentBusinessId) {
      handleFetchList(currentBusinessId)
    }
  }, [currentBusinessId])

  // Clear DROP_OFF filter when feature flag is off to avoid stale filter state
  useEffect(() => {
    if (!isDropOffEnabled && !R.isNil(filters[Filters.DROP_OFF])) {
      dispatch(
        setAppointmentTypesListFilters(R.omit([Filters.DROP_OFF], filters)),
      )
    }
  }, [isDropOffEnabled])

  const preparedList: string[] = useMemo(() => {
    const eventTypeNames = new Map()
    AppointmentEventTypes.forEach((eventType) => {
      eventTypeNames.set(eventType.id, eventType.name)
    })

    const appointmentTypeArray = getMapAsArray(map)
    const activeFilters = isDropOffEnabled
      ? filters
      : R.omit([Filters.DROP_OFF], filters)
    const filtered = applyFilters(appointmentTypeArray, activeFilters)
    const sorted = applySortings(filtered, sortings, eventTypeNames)
    const list = sorted.map((cur) => cur.id)

    return showAddAppontmentTypeRow ? [NEW_TYPE_PLACEHOLDER_ID, ...list] : list
  }, [
    showAddAppontmentTypeRow,
    map,
    filters,
    sortings,
    AppointmentEventTypes,
    isDropOffEnabled,
  ])

  const isItemLoaded = (index: number) => Boolean(preparedList[index])

  const handleApplySorting = (sortingKey: string) => {
    setShowAddAppontmentTypeRow(false)
    switch (sortings[sortingKey]) {
      case true:
        dispatch(
          setAppointmentTypesListSortings({ ...sortings, [sortingKey]: false }),
        )
        break
      case false:
        dispatch(
          setAppointmentTypesListSortings({
            ...sortings,
            [sortingKey]: undefined,
          }),
        )
        break
      default:
        dispatch(
          setAppointmentTypesListSortings({ ...sortings, [sortingKey]: true }),
        )
    }
  }

  const handleClearSortingItem = (sorting: string) => {
    const newSortings = R.omit([sorting], { ...sortings })
    dispatch(setAppointmentTypesListSortings(newSortings))
  }

  const handleApplyFilter = (filter: string, value: TableFilter) => {
    setShowAddAppontmentTypeRow(false)
    dispatch(setAppointmentTypesListFilters({ ...filters, [filter]: value }))
  }

  const handleAddNewAppointmentType = () => {
    setShowAddAppontmentTypeRow(true)
  }

  const handleCreateItemCancel = (created?: boolean) => {
    setShowAddAppontmentTypeRow(false)
    if (created && currentBusinessId) {
      handleFetchList(currentBusinessId)
    }
  }

  return (
    <Grid container item direction="column" flex={1} px={3} wrap="nowrap">
      <Grid container item justifyContent="space-between" py={2}>
        <Text variant="h1">
          {t('Admin:APPOINTMENT_CONFIGURATION.APPOINTMENT_CONFIGURATION')}
        </Text>
        {(permissions.create || !isCvcRolesEnabled) && (
          <Fab
            className={classes.addButton}
            color="inherit"
            disabled={showAddAppontmentTypeRow || isLoading}
            variant="extended"
            onClick={handleAddNewAppointmentType}
          >
            {t('Admin:APPOINTMENT_CONFIGURATION.NEW_APPOINTMENT_TYPE')}
          </Fab>
        )}
      </Grid>
      <PrimitiveTable
        HeaderProps={{
          classes: {
            tableHeader: classes.tableHeader,
          },
          useNilAsEmpty: true,
        }}
        RowComponent={AppointmentConfigurationRow}
        RowComponentProps={{
          onCreateItemCancel: handleCreateItemCancel,
        }}
        columns={columns}
        filters={addComponentsToFilters(
          isDropOffEnabled ? filters : R.omit([Filters.DROP_OFF], filters),
        )}
        isItemLoaded={isItemLoaded}
        itemSpacing={0}
        list={preparedList}
        loadMoreItems={() => {}}
        sortings={sortings}
        totalCount={preparedList.length}
        onApplyFilter={handleApplyFilter}
        onApplySorting={handleApplySorting}
        onClearFilters={handleClearFilters}
        onClearSortingItem={handleClearSortingItem}
      />
    </Grid>
  )
}

export default AppointmentConfigurationPage
